import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/apiRouting';
import authenticatedFetch from '../../utils/authenticatedFetch';
import TableChartDisplay from './TableChartDisplay';
import {
  Plus,
  Users,
  Clock,
  Phone,
  MessageSquare,
  X,
  Check,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Calendar,
  MapPin,
  Utensils,
  TrendingUp,
  Eye
} from 'lucide-react';
import './WaitlistManagement.css';

interface WaitlistEntry {
  waitlistId: string;
  storeId: string;
  partySize: number;
  customerName: string;
  customerPhone?: string;
  customerEmail?: string;
  status: string;
  priority: string;
  estimatedWaitTime: number;
  actualWaitTime?: number;
  specialRequests?: string;
  partyType: string;
  requestedTime?: string;
  createdAt: string;
  updatedAt: string;
  seatedAt?: string;
  tableAssigned?: string;
  notificationsSent: number;
  customerConfirmed?: boolean;
  confirmedAt?: string;
  customerResponse?: string;
  customerDelayed?: boolean;
  specialInstructions?: string;
  lastCustomerMessage?: string;
  lastCustomerMessageAt?: string;
  cancelledBy?: string;
}

interface TableStatus {
  storeId: string;
  tableId: string;
  tableNumber: string;
  seats: number;
  section?: string;
  status: string;
  currentParty?: {
    waitlistId: string;
    partySize: number;
    customerName: string;
    seatedAt: string;
    estimatedDiningTime: number;
    progress: string;
  };
}

interface WaitTimeEstimate {
  estimatedWaitTime: number;
  baseWaitTime: number;
  waitlistLength: number;
  confidence: string;
}

export const WaitlistManagement: React.FC = () => {
  const { user, session } = useAuth();
  const [activeTab, setActiveTab] = useState<'waitlist' | 'tables' | 'analytics'>('waitlist');
  const [selectedEnterprise, setSelectedEnterprise] = useState<string>('');
  const [selectedStore, setSelectedStore] = useState<string>('');
  const [enterprises, setEnterprises] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [waitlist, setWaitlist] = useState<WaitlistEntry[]>([]);
  const [tables, setTables] = useState<TableStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddPartyModal, setShowAddPartyModal] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);
  const [authToken, setAuthToken] = useState<string>('');
  const modalOpenedAt = useRef<number>(0);
  
  // Form state for adding party
  const [newParty, setNewParty] = useState({
    customerName: '',
    partySize: 2,
    customerPhone: '',
    customerEmail: '',
    specialRequests: '',
    partyType: 'walk-in',
    priority: 'regular',
    requestedTime: ''
  });

  // Get and maintain auth token
  useEffect(() => {
    const getToken = async () => {
      try {
        const token = await session?.getIdToken();
        const jwt = token?.getJwtToken();
        if (jwt) {
          setAuthToken(jwt);
        }
      } catch (err) {
        console.error('Failed to get auth token:', err);
      }
    };
    getToken();
    // Refresh token periodically (every 50 minutes since tokens expire after 60)
    const tokenRefresh = setInterval(getToken, 50 * 60 * 1000);
    return () => clearInterval(tokenRefresh);
  }, [session]);

  // Load enterprises when session is ready
  useEffect(() => {
    if (session) {
      loadEnterprises();
    }
  }, [session]);

  // Load stores when enterprise changes
  useEffect(() => {
    if (selectedEnterprise) {
      setStores([]); // Clear stores while loading
      setSelectedStore(''); // Clear selected store
      loadStores();
    } else {
      setStores([]);
      setSelectedStore('');
    }
  }, [selectedEnterprise]);

  // Load data when store changes
  useEffect(() => {
    if (selectedStore) {
      loadData();
      
      // Set up auto-refresh every 30 seconds
      const interval = setInterval(() => {
        loadData();
      }, 30000);
      
      setRefreshInterval(interval);
      
      return () => {
        if (interval) clearInterval(interval);
      };
    }
  }, [selectedStore]);

  const loadEnterprises = async () => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.enterprises());

      if (response.ok) {
        const data = await response.json();
        console.log('Enterprises response:', data);

        // Ensure we have an array
        let enterpriseList = [];
        if (Array.isArray(data)) {
          enterpriseList = data;
        } else if (data.data && Array.isArray(data.data)) {
          enterpriseList = data.data;
        } else if (data.Items && Array.isArray(data.Items)) {
          enterpriseList = data.Items;
        }

        setEnterprises(enterpriseList);
        if (enterpriseList.length > 0) {
          setSelectedEnterprise(enterpriseList[0].enterpriseId || enterpriseList[0].id);
        }
      } else {
        console.error('Enterprises API returned:', response.status);
      }
    } catch (error) {
      console.error('Error loading enterprises:', error);
      setEnterprises([]);
    }
  };

  const loadStores = async () => {
    if (!selectedEnterprise) return;

    try {
      const response = await authenticatedFetch(API_ENDPOINTS.enterpriseStores(selectedEnterprise));

      if (response.ok) {
        const data = await response.json();
        console.log('Stores response:', data);

        // Ensure we have an array
        let storeList = [];
        if (Array.isArray(data)) {
          storeList = data;
        } else if (data.data && Array.isArray(data.data)) {
          storeList = data.data;
        } else if (data.stores && Array.isArray(data.stores)) {
          storeList = data.stores;
        } else if (data.Items && Array.isArray(data.Items)) {
          storeList = data.Items;
        }

        setStores(storeList);
        if (storeList.length > 0) {
          setSelectedStore(storeList[0].storeId || storeList[0].id);
        }
      } else {
        console.error('Stores API returned:', response.status);
      }
    } catch (error) {
      console.error('Error loading stores:', error);
      setStores([]);
    }
  };

  const loadData = async () => {
    if (!selectedStore) return;
    
    setLoading(true);
    try {
      await Promise.all([
        loadWaitlist(),
        loadTables()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadWaitlist = async () => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.waitlistEntries(selectedStore) + '?status=waiting');
      if (response.ok) {
        const data = await response.json();
        setWaitlist(data.waitlist || []);
      }
    } catch (error) {
      console.error('Error loading waitlist:', error);
    }
  };

  const loadTables = async () => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.waitlistTables(selectedStore));
      if (response.ok) {
        const data = await response.json();
        setTables(data.tables || []);
      }
    } catch (error) {
      console.error('Error loading tables:', error);
    }
  };

  const addPartyToWaitlist = async () => {
    console.log('addPartyToWaitlist called');
    console.log('selectedStore:', selectedStore);
    console.log('newParty:', newParty);

    if (!selectedStore) {
      console.error('No store selected');
      alert('Please select a store first');
      return;
    }

    try {
      const apiUrl = API_ENDPOINTS.waitlistAdd(selectedStore);

      const response = await authenticatedFetch(apiUrl, {
        method: 'POST',
        body: JSON.stringify({
          ...newParty,
          createdBy: 'enterprise',
          createdByUserId: user?.getUsername() || 'unknown'
        })
      });

      const responseText = await response.text();

      if (response.ok) {
        const data = JSON.parse(responseText);
        await loadWaitlist();
        setShowAddPartyModal(false);

        // Reset form
        setNewParty({
          customerName: '',
          partySize: 2,
          customerPhone: '',
          customerEmail: '',
          specialRequests: '',
          partyType: 'walk-in',
          priority: 'regular',
          requestedTime: ''
        });

        // Show success message
        alert(`Added ${newParty.customerName} to waitlist. Estimated wait: ${data.estimatedWaitTime || 'N/A'} minutes`);
      } else {
        console.error('API returned error:', response.status, responseText);
        alert(`Failed to add party: ${responseText}`);
      }
    } catch (error) {
      console.error('Error adding party:', error);
      alert('Failed to add party to waitlist: ' + (error as Error).message);
    }
  };

  const seatParty = async (entry: WaitlistEntry, tableId: string) => {
    try {
      const waitlistResponse = await authenticatedFetch(API_ENDPOINTS.waitlistEntry(selectedStore, entry.waitlistId), {
        method: 'PUT',
        body: JSON.stringify({
          status: 'seated',
          tableAssigned: tableId,
          seatedAt: new Date().toISOString()
        })
      });

      if (waitlistResponse.ok) {
        await authenticatedFetch(API_ENDPOINTS.waitlistTableStatus(selectedStore, tableId), {
          method: 'PUT',
          body: JSON.stringify({
            status: 'occupied',
            currentParty: {
              customerName: entry.customerName,
              partySize: entry.partySize,
              seatedTime: new Date().toISOString()
            }
          })
        });

        await loadData();
      }
    } catch (error) {
      console.error('Error seating party:', error);
      alert('Failed to seat party');
    }
  };

  const sendNotification = async (entry: WaitlistEntry, notificationType: string = 'TABLE_READY') => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.waitlistNotify(selectedStore, entry.waitlistId), {
        method: 'POST',
        body: JSON.stringify({
          notificationType
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        alert(`SMS notification sent to ${entry.customerName}`);
      } else {
        alert(data.error || 'Failed to send notification');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      alert('Failed to send notification');
    }
  };

  const cancelWaitlistEntry = async (entry: WaitlistEntry) => {
    if (!window.confirm(`Remove ${entry.customerName} from waitlist?`)) return;

    try {
      const response = await authenticatedFetch(API_ENDPOINTS.waitlistEntry(selectedStore, entry.waitlistId), {
        method: 'PUT',
        body: JSON.stringify({
          status: 'cancelled'
        })
      });
      
      if (response.ok) {
        await loadWaitlist();
      }
    } catch (error) {
      console.error('Error cancelling waitlist entry:', error);
    }
  };

  const updateTableStatus = async (table: any, newStatus: string) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.waitlistTableStatus(selectedStore, table.tableId), {
        method: 'PUT',
        body: JSON.stringify({
          status: newStatus
        })
      });
      
      if (response.ok) {
        // TableChartDisplay will auto-refresh
        console.log(`Table ${table.tableNumber} status updated to ${newStatus}`);
      }
    } catch (error) {
      console.error('Error updating table:', error);
    }
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone || phone.length !== 10) return phone;
    return `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6)}`;
  };

  const formatWaitTime = (minutes: number) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const getTimeWaiting = (createdAt: string) => {
    const created = new Date(createdAt);
    const now = new Date();
    const minutes = Math.floor((now.getTime() - created.getTime()) / 60000);
    return minutes;
  };

  const availableTables = tables.filter(t => t.status === 'available');
  const occupiedTables = tables.filter(t => t.status === 'occupied');

  return (
    <div className="waitlist-management">
      <div className="waitlist-header">
        <div className="header-content">
          <h1>
            <Users className="icon" />
            Waitlist & Host Management
          </h1>
          <div className="header-actions">
            <select 
              value={selectedEnterprise} 
              onChange={(e) => {
                setSelectedEnterprise(e.target.value);
                setStores([]);
                setSelectedStore('');
              }}
              className="store-selector"
            >
              <option value="">Select Enterprise</option>
              {enterprises.map(enterprise => (
                <option key={enterprise.enterpriseId || enterprise.id} value={enterprise.enterpriseId || enterprise.id}>
                  {enterprise.name}
                </option>
              ))}
            </select>
            <select 
              value={selectedStore} 
              onChange={(e) => setSelectedStore(e.target.value)}
              className="store-selector"
              disabled={!selectedEnterprise}
            >
              <option value="">Select Store</option>
              {stores.map(store => (
                <option key={store.storeId || store.id} value={store.storeId || store.id}>
                  {store.name}
                </option>
              ))}
            </select>
            <button
              className="btn btn-primary"
              onClick={() => { modalOpenedAt.current = Date.now(); setShowAddPartyModal(true); }}
              disabled={!selectedStore}
            >
              <Plus size={20} />
              Add Party
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="summary-stats">
          <div className="stat-card">
            <div className="stat-value">{waitlist.length}</div>
            <div className="stat-label">In Waitlist</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{availableTables.length}</div>
            <div className="stat-label">Available Tables</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{occupiedTables.length}</div>
            <div className="stat-label">Occupied Tables</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {waitlist.length > 0 
                ? formatWaitTime(Math.round(waitlist.reduce((sum, e) => sum + e.estimatedWaitTime, 0) / waitlist.length))
                : '0 min'
              }
            </div>
            <div className="stat-label">Avg Wait Time</div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab ${activeTab === 'waitlist' ? 'active' : ''}`}
            onClick={() => setActiveTab('waitlist')}
          >
            <Users size={18} />
            Waitlist
          </button>
          <button
            className={`tab ${activeTab === 'tables' ? 'active' : ''}`}
            onClick={() => setActiveTab('tables')}
          >
            <Utensils size={18} />
            Tables
          </button>
          <button
            className={`tab ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            <TrendingUp size={18} />
            Analytics
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'waitlist' && (
          <div className="waitlist-content">
            {loading ? (
              <div className="loading">Loading waitlist...</div>
            ) : waitlist.length === 0 ? (
              <div className="empty-state">
                <Users size={48} />
                <p>No parties waiting</p>
              </div>
            ) : (
              <div className="waitlist-grid">
                {waitlist.map((entry, index) => (
                  <div key={entry.waitlistId} className={`waitlist-card${entry.customerConfirmed ? ' card-confirmed' : ''}${entry.customerDelayed ? ' card-delayed' : ''}${entry.lastCustomerMessage ? ' card-has-message' : ''}`}>
                    <div className="card-header">
                      <div className="party-info">
                        <h3>{entry.customerName}</h3>
                        <span className="party-size">Party of {entry.partySize}</span>
                        <span className={`priority priority-${entry.priority || 'regular'}`}>
                          {(entry.priority || 'regular').toUpperCase()}
                        </span>
                      </div>
                      <div className="position">#{index + 1}</div>
                    </div>
                    
                    <div className="card-body">
                      {/* Customer response status banner */}
                      {entry.customerConfirmed && (
                        <div className="customer-response confirmed">
                          <CheckCircle size={14} />
                          <span>Confirmed{entry.confirmedAt ? ` at ${new Date(entry.confirmedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : ''}</span>
                        </div>
                      )}
                      {entry.customerDelayed && !entry.customerConfirmed && (
                        <div className="customer-response delayed">
                          <AlertTriangle size={14} />
                          <span>Not ready yet - holding spot</span>
                        </div>
                      )}

                      <div className="info-row">
                        <Clock size={16} />
                        <span>Est. wait: {formatWaitTime(entry.estimatedWaitTime)}</span>
                      </div>
                      <div className="info-row">
                        <AlertCircle size={16} />
                        <span>Waiting: {formatWaitTime(getTimeWaiting(entry.createdAt))}</span>
                      </div>
                      {entry.customerPhone && (
                        <div className="info-row">
                          <Phone size={16} />
                          <span>{formatPhoneNumber(entry.customerPhone)}</span>
                        </div>
                      )}
                      {entry.specialRequests && (
                        <div className="info-row">
                          <MessageSquare size={16} />
                          <span>{entry.specialRequests}</span>
                        </div>
                      )}

                      {/* Customer SMS message */}
                      {entry.lastCustomerMessage && (
                        <div className="customer-message">
                          <MessageSquare size={14} />
                          <div className="message-content">
                            <span className="message-text">"{entry.lastCustomerMessage}"</span>
                            {entry.lastCustomerMessageAt && (
                              <span className="message-time">
                                {new Date(entry.lastCustomerMessageAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Special instructions from SMS */}
                      {entry.specialInstructions && entry.specialInstructions !== entry.lastCustomerMessage && (
                        <div className="info-row special-instructions">
                          <MessageSquare size={16} />
                          <span>Instructions: {entry.specialInstructions}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="card-actions">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => {
                          const tableId = prompt('Enter table number to seat party:');
                          if (tableId) seatParty(entry, tableId);
                        }}
                      >
                        <Check size={16} />
                        Seat
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => sendNotification(entry)}
                        disabled={!entry.customerPhone}
                        title={!entry.customerPhone ? 'No phone number available' : 'Send SMS notification'}
                      >
                        <Phone size={16} />
                        Notify
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => cancelWaitlistEntry(entry)}
                      >
                        <X size={16} />
                        Cancel
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'tables' && selectedStore && (
          <div className="tables-content">
            <div className="table-chart-container">
              <TableChartDisplay
                restaurantId={selectedStore}
                refreshInterval={10000} // Refresh every 10 seconds
                authToken={authToken}
                onTableClick={(table) => {
                  // Handle table click - could open a modal to seat party, change status, etc.
                  if (table.status === 'available' && waitlist.length > 0) {
                    const nextParty = waitlist[0];
                    if (window.confirm(`Seat ${nextParty.customerName} (party of ${nextParty.partySize}) at Table ${table.tableNumber}?`)) {
                      seatParty(nextParty, table.tableId);
                    }
                  } else if (table.status === 'occupied') {
                    if (window.confirm(`Clear Table ${table.tableNumber}?`)) {
                      updateTableStatus(table, 'dirty');
                    }
                  } else if (table.status === 'dirty') {
                    if (window.confirm(`Mark Table ${table.tableNumber} as clean?`)) {
                      updateTableStatus(table, 'available');
                    }
                  }
                }}
              />
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="analytics-content">
            <div className="analytics-placeholder">
              <TrendingUp size={48} />
              <h3>Analytics Dashboard</h3>
              <p>Wait time trends, peak hours, and performance metrics coming soon!</p>
            </div>
          </div>
        )}
      </div>

      {/* Add Party Modal */}
      {showAddPartyModal && createPortal(
        <div className="waitlist-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && Date.now() - modalOpenedAt.current > 300) setShowAddPartyModal(false); }}>
          <div className="waitlist-modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Party to Waitlist</h2>
              <button 
                className="close-button"
                onClick={() => setShowAddPartyModal(false)}
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Customer Name *</label>
                <input
                  type="text"
                  value={newParty.customerName}
                  onChange={e => setNewParty({...newParty, customerName: e.target.value})}
                  placeholder="Enter customer name"
                  required
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Party Size *</label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={newParty.partySize}
                    onChange={e => setNewParty({...newParty, partySize: parseInt(e.target.value) || 1})}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Priority</label>
                  <select
                    value={newParty.priority}
                    onChange={e => setNewParty({...newParty, priority: e.target.value})}
                  >
                    <option value="regular">Regular</option>
                    <option value="vip">VIP</option>
                    <option value="walk-in">Walk-in</option>
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    value={newParty.customerPhone}
                    onChange={e => setNewParty({...newParty, customerPhone: e.target.value})}
                    placeholder="(555) 123-4567"
                  />
                </div>
                
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={newParty.customerEmail}
                    onChange={e => setNewParty({...newParty, customerEmail: e.target.value})}
                    placeholder="customer@email.com"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Special Requests</label>
                <textarea
                  value={newParty.specialRequests}
                  onChange={e => setNewParty({...newParty, specialRequests: e.target.value})}
                  placeholder="High chair needed, birthday celebration, etc."
                  rows={3}
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={newParty.partyType}
                    onChange={e => setNewParty({...newParty, partyType: e.target.value})}
                  >
                    <option value="walk-in">Walk-in</option>
                    <option value="waitlist">Called Ahead</option>
                    <option value="reservation">Reservation</option>
                  </select>
                </div>
                
                {newParty.partyType === 'reservation' && (
                  <div className="form-group">
                    <label>Requested Time</label>
                    <input
                      type="datetime-local"
                      value={newParty.requestedTime}
                      onChange={e => setNewParty({...newParty, requestedTime: e.target.value})}
                    />
                  </div>
                )}
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowAddPartyModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={addPartyToWaitlist}
                disabled={!newParty.customerName || !newParty.partySize}
              >
                Add to Waitlist
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};